import { Platform } from 'react-native';

/**
 * Feature flags de Go Safely.
 *
 * Estos flags se compilan al bundle. Para activar/desactivar una feature en
 * producción solo cambias el valor aquí y haces deploy — sin tocar otra cosa.
 *
 * Alternativa futura: migrar a Firebase Remote Config para cambios sin redeploy.
 */

// ===========================================================================
// LOGIN ENFORCED — con ventana de gracia para review de Google Play / App Store
// ===========================================================================
/**
 * Fecha (ISO YYYY-MM-DD) a partir de la cual el LOGIN se vuelve OBLIGATORIO.
 *
 * Por qué este sistema en vez de un boolean simple:
 *   Google Play exige que su equipo de revisión pueda explorar la app SIN
 *   tener que crear cuenta (de lo contrario te rechazan el submission). Lo
 *   mismo aplica a App Store. La política sugiere dejar la app accesible
 *   los primeros 13-15 días después del submission.
 *
 *   Al pasar la fecha aquí abajo, la app activa el login obligatorio
 *   AUTOMÁTICAMENTE en TODOS los dispositivos — sin necesidad de subir
 *   una nueva versión.
 *
 * Cómo funciona:
 *   - Si HOY < LOGIN_ENFORCED_FROM → la app permite navegar como demo abierto
 *     (modo review). El equipo de Google/Apple puede ver todas las pantallas.
 *   - Si HOY >= LOGIN_ENFORCED_FROM → /auth obligatorio antes de cualquier
 *     pantalla que no sea pública.
 *
 * Cambiar este valor cuando:
 *   - Cambias la fecha de submission del próximo release
 *   - Quieres acortar/alargar la ventana de gracia
 *   - Vuelves a abrir período demo por marketing/promociones
 *
 * Set explícitamente a una fecha EN EL PASADO para activar el login YA
 * (ej. '2020-01-01'). Set a una fecha LEJANA para mantenerlo desactivado.
 */
// ── VENTANA DE REVIEW DE GOOGLE PLAY / APP STORE (submission de producción) ──
// Abrimos la "prórroga" de login para que el equipo de revisión de Google/Apple
// pueda EXPLORAR la app sin crear cuenta (requisito de la política: si no pueden
// entrar, rechazan el submission). Durante esta ventana:
//   • El revisor puede navegar todas las pantallas (mapa, comida, perfil…).
//   • Las ACCIONES reales (pedir viaje, pagar, ponerse online) SIGUEN
//     protegidas: los guards GUEST en map.tsx/wallet.tsx empujan a /auth. Así
//     que NO se expone dinero ni datos — solo la UI.
// El día LOGIN_ENFORCED_FROM el login se vuelve OBLIGATORIO para TODOS los
// dispositivos AUTOMÁTICAMENTE (evaluado en runtime, sin subir versión nueva).
//
// ESTRATEGIA: ventana AMPLIA + cierre manual por OTA al aprobar.
// La fecha se pone lejos a propósito (colchón grande) para que el modo invitado
// NUNCA se cierre a mitad del review de Google y bloquee al revisor (eso = otro
// rechazo). NO esperamos a que llegue esta fecha: el día que Google APRUEBE la
// app, cerramos el modo invitado AL INSTANTE con:
//     eas update --branch production   (tras poner una fecha pasada, ej '2020-01-01')
// Así el revisor siempre entra sin cuenta, y el login obligatorio se activa
// justo cuando NOSOTROS queremos, no cuando venza un contador.
// ═══════════════════════════════════════════════════════════════════════════
// REGLA INNEGOCIABLE — el modo invitado es PÚBLICO, nunca un modo oculto.
// ═══════════════════════════════════════════════════════════════════════════
//
// Google prohíbe expresamente que una app se comporte distinto cuando detecta
// que la revisa un empleado suyo. Un "acceso especial para revisores" es
// motivo de expulsión, no de aprobación.
//
// Por eso la ÚNICA condición que muestra el modo invitado es esta fecha, que
// va compilada en el bundle y es idéntica en el 100% de los dispositivos,
// cuentas, IPs y países. No hay — ni puede haber nunca — bifurcación por
// dispositivo, correo, IP, país ni ninguna señal que distinga a un revisor
// de un usuario real.
//
// Si en el futuro alguien necesita "que solo lo vea Google": NO. Se deja
// abierto para todos o se cierra para todos.
//
// ── POR QUÉ ESTA FECHA ES TAN LEJANA ──────────────────────────────────────
// Estaba en 2026-09-01. La app está SUSPENDIDA de Play Store: entre preparar
// la apelación, la respuesta de Google (hasta 7 días, a veces más) y una
// nueva revisión, ese plazo se pasa con facilidad.
//
// Si la fecha vence A MITAD de la revisión, el modo invitado se apaga solo,
// el revisor choca con la pantalla de login y ese es el TERCER RECHAZO — el
// que termina la cuenta de desarrollador. Un contador corriendo bajo los pies
// del revisor es un riesgo que no aporta nada.
//
// Recomendación: dejarlo encendido de forma permanente como función de
// producto. Si se apaga tras la aprobación, la siguiente subida vuelve a
// tener muro de login y obliga a cargar credenciales de prueba en
// Play Console → Contenido de la app → Acceso a la app. Si se olvida ese
// paso, el rechazo vuelve por la misma puerta.
export const LOGIN_ENFORCED_FROM = '2027-06-01';

/**
 * Cálculo derivado — NO modificar manualmente.
 * Lee LOGIN_ENFORCED_FROM y devuelve true si la fecha actual ya superó esa
 * fecha de activación.
 *
 * Compatible con el flag legacy `LOGIN_ENFORCED` (importado en _layout.tsx).
 */
export const LOGIN_ENFORCED: boolean = (() => {
  try {
    const enforceAt = new Date(LOGIN_ENFORCED_FROM + 'T00:00:00Z').getTime();
    const now = Date.now();
    return now >= enforceAt;
  } catch {
    // Si la fecha está mal formateada, fail-safe: NO enforzar para no romper
    // la app de usuarios reales por un typo en el flag.
    return false;
  }
})();

/**
 * Helper para mostrar al usuario cuánto falta para activar el login
 * (útil en banner de admin o pantalla de debug).
 */
export function getLoginEnforcedDaysRemaining(): number {
  try {
    const enforceAt = new Date(LOGIN_ENFORCED_FROM + 'T00:00:00Z').getTime();
    const diffMs = enforceAt - Date.now();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  } catch {
    return 0;
  }
}

// ===========================================================================
// KYC WALL — exige selfie + cédula antes del primer viaje del pasajero
// ===========================================================================
/**
 * Muro de seguridad KYC (selfie + cédula) antes de pedir el primer viaje.
 * Hoy está deshabilitado para QA. Al activarlo, los pasajeros nuevos
 * deben completar KYC antes de poder ver el mapa.
 */
export const KYC_WALL_ENFORCED = false;

// ===========================================================================
// PAGOS CON TARJETA (Stripe) — dependen del backend Railway
// ===========================================================================
// Hay DOS tipos de pago con tarjeta, con reglas de negocio distintas:
//
// (1) CUSTOMER_CARD_ENABLED — pago de VIAJE (pasajero→chofer) y de COMIDA
//     (cliente→restaurante) con tarjeta. Regla del fundador: esto queda FUERA.
//     Los viajes se cobran en EFECTIVO o con el LINK PROPIO del chofer (MÍO),
//     que es del chofer y NO pasa por el Stripe de la app. Se deja en false.
export const CUSTOMER_CARD_ENABLED = false;
//
// (2) PLATFORM_CARD_ENABLED — pago con tarjeta de lo que va a la cuenta de
//     Stripe del FUNDADOR: la SUSCRIPCIÓN mensual del chofer y los ANUNCIOS del
//     restaurante. Esto SÍ es un producto (y sirve para otros países).
//
//     19-sep-2026: la CUOTA del chofer ya NO depende de esta bandera: tiene la
//     suya (CUOTA_TARJETA_ENABLED, abajo). Esta queda para lo que todavía no
//     tiene camino sin Railway (anuncios del restaurante con tarjeta, guardar
//     tarjeta para cobros automáticos). Los botones que llamaban a Railway se
//     retiraron para que prender esto no los resucite.
//     (El pago de viaje sigue en efectivo/MÍO — ese es CUSTOMER_CARD_ENABLED.)
export const PLATFORM_CARD_ENABLED = false;

// (3) CUOTA_TARJETA_ENABLED — el chofer paga su cuota de servicio con tarjeta
//     desde Billetera. PAGO ÚNICO en pesos: el servidor calcula el monto
//     (crearPagoCuota), la extensión de Stripe abre el Checkout y, al cobrarse,
//     el servidor lo acredita igual que un depósito aprobado
//     (functions/src/pagoCuotaStripe.ts). Decisión del fundador, 19-sep-2026.
export const CUOTA_TARJETA_ENABLED = true;

// (4) SUSCRIPCION_STRIPE_ENABLED — la lista de PLANES recurrentes de la
//     extensión en "Mi cuota de servicio". Apagada: la cuota se paga como pago
//     único (arriba). Una suscripción de Stripe chocaría con el cobro del día 1
//     (`applyMonthlyCharge` crearía la deuda y el día 5 pausaría a quien ya
//     pagó), y los 5 productos que hay en Stripe están en USD. Además la
//     cuenta de Stripe la comparten otras apps: sin este candado, cualquier
//     producto que se sincronizara aparecería aquí.
export const SUSCRIPCION_STRIPE_ENABLED = false;

// ===========================================================================
// STORE LINKS visibles en perfil
// ===========================================================================
/**
 * Cuando los stores publiquen tu app, este flag deja de tener sentido
 * (la URL se lee dinámicamente desde configService). Mientras tanto sirve
 * para esconder el botón "Calificar la app" si aún no hay store.
 */
export const SHOW_STORE_LINKS_IN_PROFILE = true;

// ===========================================================================
// VERTICAL ESCOLAR — oculto para el envío que levanta la suspensión
// ===========================================================================
/**
 * ¿Se muestra el transporte escolar (director de escuela, rutas, alumnos)?
 *
 * POR QUÉ ESTÁ EN false — Y POR QUÉ *NO* ES POR LOS MENORES
 *
 * La duda original era de política: ¿nos aplica la Política de Familias de
 * Google por manejar datos de niños? La respuesta, verificada en el código, es
 * NO: el menor **nunca tiene cuenta**. `UserRole` no tiene STUDENT ni CHILD ni
 * MINOR; el niño se guarda como un REGISTRO bajo el uid de su padre
 * (`users/{parentId}/students`), y en las reglas siempre es `resource.data`,
 * nunca `request.auth`. No se guarda foto suya en ninguna parte. En el lenguaje
 * de Google Play eso es una app "no dirigida a niños" donde el menor es un
 * tercero titular de datos, no un usuario. Declarando el público objetivo como
 * "18 y más" no hace falta esconder nada.
 *
 * Se oculta por otra razón, más simple y más grave: **el módulo no funciona**,
 * y no funciona justo de la manera por la que Google ya nos rechazó (vc13
 * "botones que no responden" / vc16 "la app no carga").
 *
 *   · `firestore.rules` (/students) solo deja LEER al padre y al director. El
 *     chofer consulta `where('driverId','==',suUid)`, que ninguna rama permite:
 *     recibe permission-denied y la lista de alumnos de su ruta sale SIEMPRE
 *     vacía. Peor, el error se traga con `callback([])` y se disfraza de
 *     "todavía no hay alumnos".
 *   · Los botones Abordar / Entregar escriben en /students, cuyo update exige
 *     `parentId == request.auth.uid`. Al chofer le fallan siempre, en silencio.
 *   · `driverId` no lo escribe nadie: `linkStudentToDriver` existe pero no se
 *     llama desde ninguna pantalla. Aunque se arreglara la regla, la consulta
 *     seguiría sin devolver nada.
 *   · El código de invitación es un `Math.random()` que no se guarda ni se
 *     valida contra nada.
 *
 * Arreglarlo bien es construir el enlace alumno↔chofer completo, con sus reglas
 * y su UI. Eso no cabe en el envío que tiene que levantar la suspensión, y
 * mandar un vertical roto a una tercera revisión es el riesgo que no se puede
 * correr: el tercer rechazo cierra la cuenta de desarrollador.
 *
 * PARA VOLVER A ACTIVARLO basta poner `true` aquí — pero antes hay que cerrar
 * los cuatro puntos de arriba y probar el recorrido completo con dos cuentas
 * reales (padre y chofer).
 */
export const VERTICAL_ESCOLAR_VISIBLE = false;

// ===========================================================================
// VERTICAL DE LOGÍSTICA — apagado hasta que el circuito cierre
// ===========================================================================
/**
 * ¿Se muestra Logística (curier, mudanza, carga y autobús para gira)?
 *
 * El renglon nuevo que pidió el fundador el 5-oct-2026, en sus palabras: «como
 * ya ese chofer posee una guagua amplia, podemos incluir autobús para gira,
 * transporte de camioneta, logística y mudanza, y que esa persona solo pague al
 * momento de adquirir el servicio». Ocupa el hueco que deja el perfil escolar.
 *
 * ── LO QUE NO ES, Y ES LA DECISIÓN DE DISEÑO MÁS IMPORTANTE ─────────────
 *
 * **No es un rol.** Es una CAPACIDAD que se añade a la cuenta que la persona ya
 * tiene — «que se acceda directamente desde el perfil que ya el usuario de
 * pasajero tenga creado». Un rol nuevo habría obligado a tocar las siete tablas
 * rol→ruta repartidas por la app, las dos listas blancas del servidor y la
 * unión de tipos; y sobre todo habría hecho que el dueño de la camioneta
 * DEJARA de ser pasajero para ser transportista, cuando lo que quiere es las dos
 * cosas. El precedente de que esto funciona ya está en la app: la pestaña Comida
 * la ve un PASSENGER sin cambiar de rol.
 *
 * ── Y POR QUÉ NACE EN false ────────────────────────────────────────────────
 *
 * Por la misma razón medida que tiene al vertical escolar apagado: **una
 * promesa sin pantalla es exactamente el motivo por el que Google rechazó la
 * vc13**. Esta bandera se pone en `true` cuando el recorrido completo funciona
 * de punta a punta con dos cuentas reales: el cliente pide, el proveedor cotiza,
 * el cliente acepta, sale el PIN de entrega, el proveedor lo cobra en mano y la
 * comisión se devenga y se cobra. Ni un paso antes.
 *
 * Lo que NO depende de esta bandera, a propósito: la tabla de comisiones
 * (`constants/comisionLogistica.ts`) es cálculo puro y no enseña nada sola.
 */
export const VERTICAL_LOGISTICA_VISIBLE = true;
//
// ══ ENCENDIDA EL 6-OCT-2026 ═══════════════════════════════════════════
//
// Nació en `false` como puerta de release, con el criterio de «se enciende
// cuando el recorrido corra una vez contra la base real». Ese criterio era
// CIRCULAR y así se quedó un día: el recorrido no se puede correr si las
// pantallas están escondidas. El fundador lo vio al abrir la app y no encontrar
// nada.
//
// Lo que SÍ estaba comprobado al encenderla, y es lo que hace que el riesgo sea
// aceptable:
//   · Las 16 Cloud Functions, las reglas y los 8 índices del renglón,
//     desplegados y verificados uno por uno.
//   · 284 casos en verde, con los del PIN y los del dinero comprobados por
//     mutación (se rompe el código a propósito y el caso falla).
//   · Las colecciones están VACÍAS: no hay un solo usuario en este renglón, así
//     que un fallo no puede romperle nada a nadie que ya estuviera dentro.
//   · Y el motor de viajes queda intacto por construcción: cero escrituras en
//     `drivers` y en `/rides`.
//
// SI ALGO SALE MAL: esto vuelve a `false` y una OTA lo apaga en minutos. No
// hace falta tocar funciones ni reglas — las colecciones nuevas no estorban a
// nadie con la bandera apagada.

// ===========================================================================
// UBICACIÓN EN SEGUNDO PLANO — apagada hasta que Google apruebe la app
// ===========================================================================
/**
 * DECISIÓN DEL FUNDADOR (14-ago-2026): hasta pasar la revisión de Google Play,
 * la ubicación se usa SOLO mientras la app está en uso. Nada de segundo plano.
 *
 * Por qué: ACCESS_BACKGROUND_LOCATION es el permiso más castigado de la
 * revisión — exige una declaración aparte en Play Console, un vídeo con el
 * aviso destacado, y es motivo frecuente de rechazo. La app funciona sin él:
 * el chofer lleva la app abierta mientras trabaja, como en InDriver.
 *
 * ⚠️ OJO — ESTE COMENTARIO DESCRIBÍA ALGO QUE YA NO EXISTE.
 * Decía que "el servicio en primer plano con su notificación mantiene el GPS
 * vivo con la pantalla apagada". Eso YA NO PASA, y no es un detalle: hoy, con
 * la pantalla apagada o la app en segundo plano, la app DEJA DE RECIBIR
 * posiciones. No hay servicio en primer plano de ninguna clase.
 *
 * Con `false` (estado actual, verificado en android/app/src/main/AndroidManifest.xml):
 *   · nunca se pide el permiso de segundo plano (ni sale su diálogo),
 *   · no se muestra el aviso destacado (no hace falta),
 *   · `applyMode` de services/locationService.ts sale por la rama de
 *     `watchPositionAsync` y NUNCA llama a `startLocationUpdatesAsync`, que es
 *     el único sitio que pediría `foregroundService`,
 *   · el manifest quita con `tools:node="remove"` TRES permisos y UN servicio:
 *     ACCESS_BACKGROUND_LOCATION, FOREGROUND_SERVICE,
 *     FOREGROUND_SERVICE_LOCATION y el <service>
 *     expo.modules.location.services.LocationTaskService.
 *
 * PARA REACTIVARLO NO BASTA CON PONER `true` AQUÍ. Hay que devolver, EN EL
 * MISMO COMMIT, las cuatro entradas del manifest (los tres permisos y el
 * servicio: quitar su `tools:node="remove"`), porque expo-location las fusiona
 * desde su propio manifest y borrar la línea a secas no sirve — el merge las
 * repone. Sin el servicio y sus dos permisos, `startLocationUpdatesAsync`
 * revienta en Android 14+ con MissingForegroundServiceTypeException y el chofer
 * se queda sin GPS justo al ponerse en línea. Y ANTES de subir ese binario hay
 * que llenar en Play Console la declaración de ubicación en segundo plano Y la
 * de servicios en primer plano (las dos, con su vídeo); si el APK declara
 * FOREGROUND_SERVICE_LOCATION sin esa declaración, la subida se bloquea antes
 * de llegar al revisor.
 *
 * Y en el mismo commit se actualiza el punto 2.d de src/app/privacy.tsx, que
 * hoy afirma —correctamente— que la app NO recoge ubicación en segundo plano.
 */
export const UBICACION_SEGUNDO_PLANO = false;

// ===========================================================================
// COBROS DENTRO DE LA APP — APAGADOS EN iOS, Y NO ES UNA PREFERENCIA
// ===========================================================================
/**
 * En iPhone, Go Safely NO COBRA NADA ni invita a pagar en ningún sitio.
 *
 * ── POR QUÉ ──────────────────────────────────────────────────────────────
 *
 * La regla 3.1.1 de la App Store obliga a usar la compra de Apple para todo lo
 * que «desbloquee funciones dentro de la app», y prohíbe expresamente los
 * «botones, enlaces externos u otras llamadas a la acción que dirijan a formas
 * de pago distintas».
 *
 * Y la membresía del chofer es exactamente eso, por mucho que se pague fuera.
 * No lo decide una opinión: lo dice el código. Lo que el chofer compra con sus
 * RD$1.000–2.500 no es un bien ni un servicio que se le preste fuera de la app
 * — es un booleano, `driverBlockedNoPayment`. Mientras está en `true`,
 * `foodDispatch.ts` le rechaza los pedidos y `map.tsx` no le deja ponerse en
 * línea. Al pagar, `aplicarSaldosALaCuota` lo pone en `false` y las funciones
 * vuelven. Eso es «unlock features within your app» con todas las letras.
 *
 * La otra mitad SÍ está bien y no se toca: el viaje y la comida se pagan en
 * efectivo o por transferencia directo al chofer o al restaurante, y la app no
 * los procesa. Esa es la excepción legítima del mundo físico (3.1.3(e)).
 *
 * ── QUÉ SE HACE EN SU LUGAR ──────────────────────────────────────────────
 *
 * Lo mismo que hacen Netflix o Spotify, al amparo de la 3.1.3(f): en iOS la
 * pantalla solo INFORMA del estado («tu membresía no está al día»), sin precio,
 * sin botón, sin cuentas bancarias, sin cifra a depositar y sin enlace a
 * ninguna parte. El chofer paga donde siempre: en la web, que ya existe.
 *
 * Decisión del fundador (3-oct-2026), sobre la alternativa de implementar la
 * suscripción de Apple: esa se lleva entre un 15% y un 30% de CADA membresía
 * todos los meses, y este negocio vive de margen bajo y volumen.
 *
 * ── POR QUÉ `Platform.OS` Y NO UNA BANDERA DE SERVIDOR ───────────────────
 *
 * ⚠️ ESTO NO PUEDE SER UNA BANDERA QUE SE ENCIENDA DESDE FIRESTORE NI POR OTA.
 * Aprobar una app sin cobros y encenderlos después es «bait and switch»: la
 * regla 2.3.1 lo trata como engañar al revisor, y cuesta la cuenta de
 * desarrollador entera, no un rechazo. `Platform.OS` se decide en el teléfono
 * y no hay forma de darle la vuelta a distancia.
 *
 * ── ANDROID Y WEB NO CAMBIAN EN NADA ─────────────────────────────────────
 *
 * Aquí vale `true` y todo se comporta EXACTAMENTE igual que hoy. Esta constante
 * solo tiene efecto dentro de un iPhone.
 */
export const COBROS_EN_LA_APP = Platform.OS !== 'ios';
